/** Screenshot thumbnails, the paste and drop zone and its error (spec rows 11 to 15). */
import { useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Kbd, KbdGroup } from '@/components/ui/kbd';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { MAX_IMAGES } from './constants';
import type { DraftImage } from './types';

const isMac = (): boolean => {
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  return /mac/i.test(nav.userAgentData?.platform ?? nav.platform ?? '');
};

interface Props {
  images: DraftImage[];
  hot: boolean;
  pasteError: string | null;
  disabled: boolean;
  onRemove: (id: string, index: number) => void;
}

function PasteHint({ hot, full, modifier }: Readonly<{ hot: boolean; full: boolean; modifier: string }>) {
  if (hot) return <>Drop to attach</>;
  if (full) return <>{`${MAX_IMAGES} of ${MAX_IMAGES} attached. Remove one to add another.`}</>;
  return (
    <>
      Paste a screenshot (
      <KbdGroup>
        <Kbd>{modifier}</Kbd>+<Kbd>V</Kbd>
      </KbdGroup>
      ) or drop an image here
    </>
  );
}

export function ScreenshotSection({ images, hot, pasteError, disabled, onRemove }: Readonly<Props>) {
  const full = images.length >= MAX_IMAGES;
  const modifier = isMac() ? '⌘' : 'Ctrl';
  const hint = useRef('feedback-paste-hint');

  return (
    <>
      <div className="flex justify-between text-xs font-medium text-muted-foreground">
        <span>Screenshots</span>
        <span data-testid="feedback-shots-count">
          {images.length} / {MAX_IMAGES}
        </span>
      </div>
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((image, i) => (
            <figure
              key={image.id}
              data-testid={`feedback-thumb-${i + 1}`}
              className="relative h-12 w-16 overflow-hidden rounded-md border border-border"
            >
              {image.objectUrl ? (
                <img src={image.objectUrl} alt={`Screenshot ${i + 1}`} className="size-full object-cover" />
              ) : (
                <Skeleton className="size-full" />
              )}
              <Button
                type="button"
                variant="secondary"
                size="icon-xs"
                className="absolute top-0.5 right-0.5"
                aria-label={`Remove screenshot ${i + 1}`}
                data-testid={`feedback-thumb-remove-${i + 1}`}
                disabled={disabled}
                onClick={() => onRemove(image.id, i)}
              >
                <X aria-hidden />
              </Button>
            </figure>
          ))}
        </div>
      )}
      <div
        role="group"
        tabIndex={full ? -1 : 0}
        aria-label="Screenshot paste area"
        aria-describedby={hint.current}
        aria-disabled={full || undefined}
        data-testid="feedback-paste-zone"
        className={cn(
          'rounded-md border border-dashed border-muted-foreground bg-card p-2 text-center text-xs text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
          full && 'opacity-60',
          hot && 'border-primary bg-primary/10 text-foreground',
        )}
      >
        <span id={hint.current}>
          <PasteHint hot={hot} full={full} modifier={modifier} />
        </span>
      </div>
      {pasteError && (
        <p role="alert" data-testid="feedback-paste-error" className="text-xs text-destructive-text">
          {pasteError}
        </p>
      )}
    </>
  );
}
